// useUserSettings.ts
import { useEffect } from 'react';
import { UserProfileDataTypes, UpdateUserProfileSettinsPayload, UserProfileData } from 'types/userProfile';
import { useDispatch, useSelector } from 'react-redux';
import { SetSingleUserSetting } from '../store/reducers/userSettingsSlice';
import { RootState } from 'store';
import { ClearUserSettingFromSession } from 'utils/user-settings';

const settingsToStore = [
  'DefaultRecordsPerPage',
  'DateTimeFormat',
  'DefaultReportFormat',
  'EmailNotification',
  'TaggingToolBar',
  'CurrentLandingPage'
] as const;

export const useUserSettings = () => {
  const dispatch = useDispatch();

  // Get the user settings from redux
  const userSettings = useSelector((state: RootState) => state.userSettings);

  // 🔁 BroadcastChannel listener - one-time setup
  useEffect(() => {
    const channel = new BroadcastChannel('user-settings');

    channel.onmessage = (event) => {
      const { type, key, value } = event.data;

      if (type === 'update' && key && value) {
        sessionStorage.setItem(key, value);
        dispatch(SetSingleUserSetting({ key, value }));
      } else if (type === 'remove') {
        ClearUserSettingFromSession();
      }
    };

    return () => {
      channel.close();
    };
  }, [dispatch]);

  /*
   Set the user Data in session and same time we are updatig redux as well as other session.  
  */
  const SetUserSetting = async (userSettingData: UserProfileDataTypes, data?: UpdateUserProfileSettinsPayload[]) => {
    if (!userSettingData) return;

    const channel = new BroadcastChannel('user-settings');
    try {
      let configMap: Map<string, string> | undefined;

      if (data) {
        configMap = new Map(
          data.map(({ configuredPropertyKey, configuredPropertyValue }) => [configuredPropertyKey, configuredPropertyValue])
        );
      }

      settingsToStore.forEach((key) => {
        const value = data && configMap ? configMap.get(key) : userSettingData[key]?.value;

        if (value !== undefined && value !== null) {
          let finalValue = value.toString();

          if (key === 'DefaultReportFormat' || key === 'DateTimeFormat') {
            const matchedOption = userSettingData[key]?.options.find((x) => x.id.toString() === value.toString());
            if (matchedOption) {
              finalValue = matchedOption.value.toString();
            }
          } else if (key === 'CurrentLandingPage') {
            const matchedOption = userSettingData[key]?.options.find((x) => x.id.toString() === value.toString());
            if (matchedOption) {
              finalValue = matchedOption.id.toString();
            }
          }

          sessionStorage.setItem(key, finalValue);
          dispatch(SetSingleUserSetting({ key, value: finalValue }));
          channel.postMessage({ type: 'update', key, value: finalValue });
        }
      });
    } catch (e) {
      console.error(e);
    }
  };

  /*
  For get user setting use for only utils funcation. remaining places we are using redux.  
  */
  const GetUserCustomSettings = (): UserProfileData => {
    const DefaultRecordsPerPage = sessionStorage.getItem('DefaultRecordsPerPage');
    const DateTimeFormat = sessionStorage.getItem('DateTimeFormat');
    const DefaultReportFormat = sessionStorage.getItem('DefaultReportFormat');
    const EmailNotification = sessionStorage.getItem('EmailNotification');
    const TaggingToolBar = sessionStorage.getItem('TaggingToolBar');
    const CurrentLandingPage = sessionStorage.getItem('CurrentLandingPage');

    return {
      DefaultRecordsPerPage: DefaultRecordsPerPage ?? null,
      DateTimeFormat: DateTimeFormat ?? null,
      DefaultReportFormat: DefaultReportFormat ?? null,
      EmailNotification: EmailNotification ?? null,
      TaggingToolBar: TaggingToolBar ?? null,
      CurrentLandingPage: CurrentLandingPage ?? null
    };
  };

  /*
  For check session is pdated or not  
  */
  const isUserSettingsInitialized = !!sessionStorage.getItem('DefaultRecordsPerPage');

  const getUserRoutingURL = (CurrentLandingPage: string, lastVisitedURL: string): string => {
    let routingURL: string = '';
    if (CurrentLandingPage === '' || lastVisitedURL?.includes('/login')) {
      CurrentLandingPage = 'DAA';
    }
    switch (CurrentLandingPage) {
      case '-1':
        routingURL = lastVisitedLocation;
        break;
      case 'DAA':
        routingURL = '/dashboard/admin';
        break;
      case 'DLA':
        routingURL = '/design/document-list';
        break;
      case 'MDA':
        routingURL = '/design/master-data';
        break;
      case 'MMT':
        routingURL = '/training/model-training';
        break;
      case 'CMA':
        routingURL = '/training/confidence-model';
        break;
      case 'MMM':
        routingURL = '/training/model-management';
        break;
      case 'QPA':
        routingURL = '/review/prediction';
        break;
      case 'QAA':
        routingURL = '/review/assurance';
        break;
      case 'QEA':
        routingURL = '/review/exception';
        break;
      case 'QVA':
        routingURL = '/review/verification';
        break;
      case 'RAR':
        routingURL = '/report/process-report';
        break;
      case 'RSR':
        routingURL = '/report/system-report';
        break;
      case 'AMP':
        routingURL = '/admin/model-portablilty';
        break;
      case 'AMA':
        routingURL = '/admin/access-management';
        break;
      case 'CSA':
        routingURL = '/admin/client-settings';
        break;
      default:
        routingURL = '/dashboard/admin';
        break;
    }

    return routingURL;
  };

  const getCurrentLandingPageFromSession: string = sessionStorage.getItem('CurrentLandingPage') || '';

  const lastVisitedLocation:string = localStorage.getItem('last_location') || '/dashboard/admin';

  return {
    GetUserCustomSettings,
    SetUserSetting,
    userSettings,
    isUserSettingsInitialized,
    getUserRoutingURL,
    getCurrentLandingPageFromSession,
    lastVisitedLocation
  };
};
